import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const HeroSection = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);

    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e?.clientX / window.innerWidth) * 100,
        y: (e?.clientY / window.innerHeight) * 100
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Add scroll to bottom handler
  const scrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement?.scrollHeight,
      behavior: 'smooth'
    });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="/assets/images/home-background.png"
          alt="Ayurvedic Wellness Background"
          className="w-full h-full object-cover"
        />
        {/* Semi-transparent overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-background/80 via-background/70 to-background/60"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        <div>
          <h1
            className="font-headline text-2xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight"
            style={{
              background: 'linear-gradient(45deg, var(--color-primary), var(--color-accent), var(--color-primary))',
              backgroundSize: '200% 200%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Protocol-Driven and Results-Oriented Ayurvedic Care for Chronic & Autoimmune Conditions.
          </h1>
        </div>

        <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed"
        >
          Structured Ayurvedic care backed by clinical insight — delivering personalized Diet, Exercise and Treatment plans for Psoriasis, Rheumatoid Arthritis, Crohn's Disease, PCOD, Thyroid issues, Obesity, Diabetes, Spine/Disc problems, Acidity and IBS etc.,
        </p>

        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <Button
            variant="default"
            size="lg"
            className="cta-morph ayurveda-gradient hover:from-primary/90 hover:to-secondary/90 text-white font-cta font-semibold px-8 py-4 text-lg shadow-floating w-full"
            iconName="Calendar"
            iconPosition="left"
            iconSize={20}
            onClick={() => window.location.href = '/specialist-booking'}
          >
            Book Consultation
          </Button>

          <Button
            variant="outline"
            size="lg"
            className="glass-panel border-primary/30 text-primary hover:bg-primary/10 font-medium px-8 py-4 text-lg w-full"
            iconName="MapPin"
            iconPosition="left"
            iconSize={20}
            onClick={() => window.open('https://maps.app.goo.gl/8aeG871Y4R1RtPxY9', '_blank')}
          >
            Visit Our Clinic
          </Button>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-2xl mx-auto mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          {[
            { number: "10K+", label: "Patients Healed" },
            { number: "30+", label: "Years Experience" },
            { number: "95%", label: "Success Rate" },
          ]?.map((stat, index) => (
            <motion.div
              key={index}
              className="glass-card p-4 text-center"
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-2xl font-bold text-primary mb-1">
                {stat?.number}
              </div>
              <div className="text-sm text-muted-foreground">
                {stat?.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
      {/* Scroll Indicator */}
      <motion.div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 cursor-pointer"
        initial={{ opacity: 0 }}
        animate={isVisible ? { opacity: 1, y: [0, 10, 0] } : {}}
        transition={{ opacity: { duration: 1, delay: 1 }, y: { duration: 2, repeat: Infinity, ease: "easeInOut" } }}
        onClick={scrollToBottom}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }} >
        <div className="glass-panel p-3 rounded-full hover:bg-primary/10 transition-colors"> <Icon name="ChevronDown" size={24} className="text-primary" />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;